/**
 * BaseSyncAdapter - Base class for all sync adapters
 * 
 * CHANGE HISTORY:
 * - 251026_0655 [pending] danielbeer: Created TypeScript version with enhanced API
 */

import type { SyncChange, SyncDirection, ConflictResolution } from '../types.js';

export interface SyncAdapterCapabilities {
  pull: boolean;
  push: boolean;
  realtime: boolean;
  deltaSync: boolean;
  batchSync: boolean;
}

export abstract class BaseSyncAdapter {
  protected name: string;
  protected direction: SyncDirection;
  protected conflictResolution: ConflictResolution;
  protected entityTypes: string[];

  constructor(config: {
    name?: string;
    direction?: SyncDirection;
    conflictResolution?: ConflictResolution;
    entityTypes?: string[];
  } = {}) {
    this.name = config.name || 'unknown';
    this.direction = config.direction || 'both';
    this.conflictResolution = config.conflictResolution || 'last-write-wins';
    this.entityTypes = config.entityTypes || [];
  }

  /**
   * Pull changes from remote
   * @param since - Timestamp of last sync
   * @returns Array of changes
   */
  abstract pull(since: number): Promise<SyncChange[]>;

  /**
   * Push changes to remote
   * @param change - Change to push
   * @returns Success status
   */
  abstract push(change: SyncChange): Promise<boolean>;

  /**
   * Check if real-time sync is supported
   */
  supportsRealtime(): boolean {
    return false;
  }

  /**
   * Start real-time sync
   */
  startRealtime(callback: (change: SyncChange) => void): void {
    throw new Error('Real-time sync not supported');
  }

  /**
   * Stop real-time sync
   */
  stopRealtime(): void {
    // Override if needed
  }

  /**
   * Get adapter capabilities
   */
  getCapabilities(): SyncAdapterCapabilities {
    return {
      pull: this.direction === 'pull' || this.direction === 'both',
      push: this.direction === 'push' || this.direction === 'both',
      realtime: this.supportsRealtime(),
      deltaSync: false,
      batchSync: false,
    };
  }

  /**
   * Get adapter name
   */
  getName(): string {
    return this.name;
  }

  /**
   * Get sync direction
   */
  getDirection(): SyncDirection {
    return this.direction;
  }

  /**
   * Get conflict resolution strategy
   */
  getConflictResolution(): ConflictResolution {
    return this.conflictResolution;
  }

  /**
   * Get entity types this adapter syncs
   */
  getEntityTypes(): string[] {
    return this.entityTypes;
  }

  /**
   * Set entity types this adapter syncs
   */
  setEntityTypes(types: string[]): void {
    this.entityTypes = types;
  }
}

export default BaseSyncAdapter;
